import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api, { analyticsAPI, projectAPI, eventAPI, notificationAPI, crewAPI, assignmentAPI, leadAPI } from '../utils/api';
import { formatDate } from '../utils/helpers';
import { Calendar, CalendarPlus, Users, Briefcase, Edit, Send, Mail, X, UserPlus, Phone, MessageSquare, Inbox, Bell } from 'lucide-react';
import { toast } from 'sonner';
import PendingConfirmationsAlert from '../components/PendingConfirmationsAlert';
import TodoCard from '../components/TodoCard';
import { motion } from 'framer-motion';

// Generate Google Calendar URL for adding event
const generateCalendarUrl = (event, project) => {
  const eventDate = event.event_date.replace(/-/g, '');
  
  const parseTime = (timeStr) => {
    if (!timeStr) return '090000';
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, '0')}${(minutes || 0).toString().padStart(2, '0')}00`;
  };
  
  const startTime = parseTime(event.start_time);
  const endTime = parseTime(event.end_time);
  
  const coupleName = project?.bride_name && project?.groom_name ? ` - ${project.bride_name} & ${project.groom_name}` : '';
  const title = encodeURIComponent(`Ami Video - ${event.event_name}${coupleName}`);
  const location = encodeURIComponent(event.venue_address || event.location || '');
  const details = encodeURIComponent(`Event Type: ${event.event_type}\n${event.event_notes || ''}`);
  
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${eventDate}T${startTime}/${eventDate}T${endTime}&details=${details}&location=${location}`;
};

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [allUpcomingEvents, setAllUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventDetails, setEventDetails] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  // Edit event state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  // Notification state (for app events with assignments)
  const [showNotifyPanel, setShowNotifyPanel] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [sendingNotification, setSendingNotification] = useState(false);
  // Quick email state (for Google Calendar events - no assignments needed)
  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [crewMembers, setCrewMembers] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  // Assign crew state (for app events)
  const [showAssignPanel, setShowAssignPanel] = useState(false);
  const [assignments, setAssignments] = useState([]);

  const fetchAllData = useCallback(async () => {
    try {
      let appEvents = [];
      let analyticsData = null;

      if (isAdmin) {
        const response = await analyticsAPI.getDashboard();
        analyticsData = response.data;
        appEvents = (analyticsData.upcoming_events || []).map(e => ({
          ...e,
          source: 'app'
        }));
        try {
          const ls = await leadAPI.stats();
          setLeadStats(ls.data);
        } catch { /* non-fatal */ }
      } else {
        const eventsResponse = await eventAPI.getAll();
        const todayStr = new Date().toISOString().split('T')[0];
        appEvents = eventsResponse.data
          .filter(e => e.event_date >= todayStr)
          .map(e => ({ ...e, source: 'app' }));
        analyticsData = { upcoming_events: appEvents };
      }

      let googleEvents = [];
      try {
        const gRes = await api.get('/api/calendar/google-events');
        const todayStr = new Date().toISOString().split('T')[0];
        googleEvents = (gRes.data.events || [])
          .filter(e => e.event_date >= todayStr)
          .map(e => ({ ...e, source: 'google_calendar' }));
      } catch (err) {
        // Google Calendar not connected - non-fatal
        console.debug('Google Calendar not connected:', err?.message);
      }

      const combined = [...appEvents, ...googleEvents].sort(
        (a, b) => (a.event_date || '').localeCompare(b.event_date || '')
      );

      // Filter to next 7 days only
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sevenDaysLater = new Date(today);
      sevenDaysLater.setDate(sevenDaysLater.getDate() + 7);
      const todayStr = today.toISOString().split('T')[0];
      const sevenDaysStr = sevenDaysLater.toISOString().split('T')[0];

      const next7Days = combined.filter(e => 
        e.event_date >= todayStr && e.event_date <= sevenDaysStr
      );

      setAnalytics(analyticsData);
      setAllUpcomingEvents(next7Days);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleEventClick = async (event) => {
    try {
      const eventRes = await eventAPI.getOne(event.event_id);
      let project = null;
      if (eventRes.data.project_id) {
        try {
          const projectRes = await projectAPI.getOne(eventRes.data.project_id);
          project = projectRes.data;
        } catch (err) {
          // Standalone event (no project) - non-fatal
          console.debug('No project linked to event:', err?.message);
        }
      }
      const assignmentsRes = await eventAPI.getAssignments(event.event_id);
      setEventDetails({
        event: eventRes.data,
        project,
        assignments: assignmentsRes.data
      });
      setIsEditing(false);
      setShowNotifyPanel(false);
      setShowAssignPanel(false);
      setShowEventModal(true);
    } catch (error) {
      console.error('Failed to fetch event details:', error);
      toast.error('Failed to load event details');
    }
  };

  const startEditing = () => {
    setEditFormData({
      event_name: eventDetails.event.event_name || '',
      event_type: eventDetails.event.event_type || 'Wedding',
      event_date: eventDetails.event.event_date || '',
      start_time: eventDetails.event.start_time || '',
      end_time: eventDetails.event.end_time || '',
      location: eventDetails.event.location || '',
      venue_address: eventDetails.event.venue_address || '',
      event_notes: eventDetails.event.event_notes || ''
    });
    setIsEditing(true);
    setShowNotifyPanel(false);
  };

  const saveEvent = async () => {
    try {
      await eventAPI.update(eventDetails.event.event_id, editFormData);
      toast.success('Event updated!');
      // Refresh event details
      const eventRes = await eventAPI.getOne(eventDetails.event.event_id);
      setEventDetails(prev => ({ ...prev, event: eventRes.data }));
      setIsEditing(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const openNotifyPanel = async () => {
    setShowNotifyPanel(true);
    setIsEditing(false);
    setSelectedAssignments(eventDetails.assignments.map(a => a.assignment_id));
    try {
      const res = await notificationAPI.getConfig();
      setNotificationConfig(res.data);
    } catch {
      setNotificationConfig(null);
    }
  };

  const sendNotification = async (type) => {
    if (selectedAssignments.length === 0) {
      toast.error('No crew members selected');
      return;
    }
    setSendingNotification(true);
    try {
      const response = await notificationAPI.send(selectedAssignments, type);
      const { results } = response.data;
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (successful > 0) toast.success(`${successful} notification(s) sent!`);
      failed.forEach(f => toast.error(`Failed: ${f.crew_name} - ${f.error}`));
      setShowNotifyPanel(false);
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setSendingNotification(false);
    }
  };

  const sendReminderPush = async () => {
    if (selectedAssignments.length === 0) {
      toast.error('No crew members selected');
      return;
    }
    setSendingNotification(true);
    try {
      const response = await notificationAPI.sendReminder(selectedAssignments);
      toast.success(`Reminder pushed to ${response.data.sent} crew 🔔`);
      setShowNotifyPanel(false);
    } catch {
      toast.error('Failed to send reminder');
    } finally {
      setSendingNotification(false);
    }
  };

  // Quick email crew (for Google Calendar events - no assignment/event creation)
  const openEmailPanel = async () => {
    setShowEmailPanel(true);
    setIsEditing(false);
    setShowNotifyPanel(false);
    setShowAssignPanel(false);
    setSelectedCrewIds([]);
    try {
      const [crewRes, configRes] = await Promise.all([
        crewAPI.getAll(),
        notificationAPI.getConfig()
      ]);
      setCrewMembers(crewRes.data);
      setNotificationConfig(configRes.data);
    } catch {
      toast.error('Failed to load data');
    }
  };

  const sendQuickNotification = async (type) => {
    if (selectedCrewIds.length === 0) {
      toast.error('Select at least one crew member');
      return;
    }
    setSendingNotification(true);
    try {
      const response = await api.post('/api/notifications/quick-send', {
        crew_ids: selectedCrewIds,
        notification_type: type,
        event_info: {
          event_name: eventDetails.event.event_name,
          event_date: eventDetails.event.event_date,
          start_time: eventDetails.event.start_time,
          end_time: eventDetails.event.end_time,
          location: eventDetails.event.location || eventDetails.event.venue_address || ''
        }
      });
      const { results } = response.data;
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      if (successful > 0) toast.success(`${successful} ${type} notification(s) sent!`);
      failed.forEach(f => toast.error(`Failed: ${f.crew_name} - ${f.error}`));
      setShowEmailPanel(false);
    } catch {
      toast.error('Failed to send emails');
    } finally {
      setSendingNotification(false);
    }
  };

  // Assign crew (for app events only)
  const openAssignPanel = async () => {
    setShowAssignPanel(true);
    setIsEditing(false);
    setShowNotifyPanel(false);
    setAssignments([{ crew_member_id: '', day_type: 'full_day', custom_amount_usd: 0, custom_amount_inr: 0 }]);
    try {
      const res = await crewAPI.getAll();
      setCrewMembers(res.data);
    } catch {
      toast.error('Failed to load crew members');
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    const validAssignments = assignments.filter(a => a.crew_member_id);
    if (validAssignments.length === 0) {
      toast.error('Select at least one crew member');
      return;
    }
    try {
      await assignmentAPI.create({
        event_id: eventDetails.event.event_id,
        crew_assignments: validAssignments
      });
      toast.success('Crew assigned!');
      const assignmentsRes = await eventAPI.getAssignments(eventDetails.event.event_id);
      setEventDetails(prev => ({ ...prev, assignments: assignmentsRes.data }));
      setShowAssignPanel(false);
    } catch {
      toast.error('Failed to assign crew');
    }
  };

  // Google Calendar event click
  const handleGoogleEventClick = (event) => {
    setEventDetails({
      event: {
        event_name: event.event_name,
        event_type: 'Google Calendar',
        event_date: event.event_date,
        start_time: event.start_time,
        end_time: event.end_time,
        location: event.location || '',
        venue_address: event.location || '',
        event_notes: '',
        google_event_id: event.google_event_id,
        google_link: event.html_link || ''
      },
      project: null,
      assignments: [],
      isGoogleEvent: true
    });
    setIsEditing(false);
    setShowNotifyPanel(false);
    setShowEventModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 lg:p-8" data-testid="dashboard-loading">
          <div className="mb-8">
            <div className="h-9 w-48 bg-black/5 rounded-2xl animate-pulse mb-3" />
            <div className="h-4 w-64 bg-black/5 rounded-full animate-pulse" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-8">
            {[0, 1, 2].map(i => (
              <div key={i} className="card p-6 h-32 animate-pulse" />
            ))}
          </div>
          <div className="card p-6">
            <div className="h-6 w-40 bg-black/5 rounded-full animate-pulse mb-5" />
            <div className="space-y-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="h-20 bg-secondary rounded-2xl animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="dashboard">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-1">Dashboard</h1>
          <p className="text-muted-foreground text-base">Welcome back to Ami Video Crew Scheduler</p>
        </div>

        {isAdmin && <PendingConfirmationsAlert />}

        {isAdmin && analytics && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8"
          >
            <div
              className="card p-4 sm:p-6 cursor-pointer hover:border-gold/50 transition-colors"
              onClick={() => navigate('/leads')}
              data-testid="stat-leads"
            >
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Inbox className="text-gold" size={20} />
                {leadStats && leadStats.conversion_rate != null && (
                  <span className="text-xs font-medium text-emerald-500">{leadStats.conversion_rate}% booked</span>
                )}
              </div>
              <p className="text-3xl sm:text-4xl font-semibold tracking-tight mb-1">{leadStats ? leadStats.new_this_month : 0}</p>
              <p className="text-sm font-medium text-muted-foreground">New Leads · This Month</p>
            </div>

            <div 
              className="card p-4 sm:p-6 cursor-pointer hover:border-gold/50 transition-colors" 
              onClick={() => navigate('/projects')}
              data-testid="stat-total-projects"
            >
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Briefcase className="text-gold" size={20} />
              </div>
              <p className="text-3xl sm:text-4xl font-semibold tracking-tight mb-1">{analytics.total_projects}</p>
              <p className="text-sm font-medium text-muted-foreground">Projects</p>
            </div>

            <div 
              className="card p-4 sm:p-6 cursor-pointer hover:border-gold/50 transition-colors" 
              onClick={() => navigate('/crew')}
              data-testid="stat-total-crew"
            >
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Users className="text-gold" size={20} />
              </div>
              <p className="text-3xl sm:text-4xl font-semibold tracking-tight mb-1">{analytics.total_crew}</p>
              <p className="text-sm font-medium text-muted-foreground">Crew Members</p>
            </div>

            <div 
              className="card p-4 sm:p-6 col-span-2 lg:col-span-1 cursor-pointer hover:border-gold/50 transition-colors" 
              onClick={() => navigate('/calendar')}
              data-testid="stat-upcoming-events"
            >
              <div className="flex items-center justify-between mb-2 sm:mb-4">
                <Calendar className="text-gold" size={20} />
              </div>
              <p className="text-3xl sm:text-4xl font-semibold tracking-tight mb-1">{analytics.upcoming_events_count}</p>
              <p className="text-sm font-medium text-muted-foreground">Upcoming Events</p>
            </div>
          </motion.div>
        )}

        {isAdmin && <TodoCard />}

        <div className="card p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-4 sm:mb-6">Upcoming Events</h2>
          
          {allUpcomingEvents.length > 0 ? (
            <div className="space-y-3">
              {allUpcomingEvents.map((event, idx) => {
                const isGoogle = event.source === 'google_calendar';
                const key = isGoogle ? event.google_event_id : event.event_id;
                
                return (
                  <motion.div 
                    key={key || `idx-${idx}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: Math.min(idx * 0.04, 0.4), ease: 'easeOut' }}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-2xl border transition-all active:scale-[0.99] cursor-pointer ${
                      isGoogle 
                        ? 'border-blue-100 dark:border-blue-500/30 bg-blue-50/40 hover:bg-blue-50 dark:bg-blue-500/10' 
                        : 'border-border bg-secondary hover:bg-secondary'
                    }`}
                    onClick={() => isGoogle ? handleGoogleEventClick(event) : handleEventClick(event)}
                    data-testid={`upcoming-event-${key || idx}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-lg">
                          {!isGoogle && event.bride_name && event.groom_name 
                            ? `${event.bride_name} & ${event.groom_name}`
                            : event.event_name}
                        </h3>
                        {isGoogle && (
                          <span className="text-[10px] px-2 py-0.5 bg-blue-100 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                            Google Calendar
                          </span>
                        )}
                      </div>
                      {!isGoogle && (
                        <p className="text-sm text-gold">{event.event_type} &bull; {event.event_name}</p>
                      )}
                      {event.location && (
                        <p className="text-xs text-muted-foreground mt-1">{event.location}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{formatDate(event.event_date)}</p>
                      {event.start_time && event.end_time && (
                        <p className="text-xs text-muted-foreground">{event.start_time} - {event.end_time}</p>
                      )}
                      {(event.venue_address || event.location) && !isGoogle && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue_address || event.location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs text-gold hover:underline flex items-center gap-1 justify-end mt-1"
                          data-testid={`directions-${key}`}
                        >
                          Get Directions
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground">No upcoming events</p>
          )}
        </div>
      </div>

      {/* Event Details Modal */}
      {showEventModal && eventDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={() => setShowEventModal(false)}>
          <div className="bg-background w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto p-5 sm:p-8 rounded-t-2xl sm:rounded-none" onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-serif mb-2">{eventDetails.event.event_name}</h2>
                <span className="inline-block px-3 py-1 bg-gold/10 text-gold text-sm">
                  {eventDetails.event.event_type}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {isAdmin && !isEditing && !eventDetails.isGoogleEvent && (
                  <>
                    <button 
                      onClick={startEditing}
                      className="flex items-center gap-1.5 px-3 py-2 border border-border hover:border-gold transition-colors text-xs"
                      data-testid="modal-edit-event-btn"
                    >
                      <Edit size={14} /> <span className="sm:hidden lg:inline">Edit</span>
                    </button>
                    <button 
                      onClick={openAssignPanel}
                      className="flex items-center gap-1.5 px-3 py-2 border border-border hover:border-gold transition-colors text-xs"
                      data-testid="modal-assign-crew-btn"
                    >
                      <UserPlus size={14} /> <span className="sm:hidden lg:inline">Assign</span>
                    </button>
                    {eventDetails.assignments.length > 0 && (
                      <button 
                        onClick={openNotifyPanel}
                        className="flex items-center gap-1.5 px-3 py-2 border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors text-xs"
                        data-testid="modal-notify-crew-btn"
                      >
                        <Send size={14} /> <span className="sm:hidden lg:inline">Notify</span>
                      </button>
                    )}
                  </>
                )}
                {eventDetails.isGoogleEvent && isAdmin && (
                  <>
                    <button 
                      onClick={openEmailPanel}
                      className="flex items-center gap-1.5 px-3 py-2 border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors text-xs"
                      data-testid="modal-email-crew-btn"
                    >
                      <Send size={14} /> <span className="sm:hidden lg:inline">Notify</span>
                    </button>
                    <a
                      href={eventDetails.event.google_link || `https://calendar.google.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 border border-blue-300 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:bg-blue-500/10 transition-colors"
                      title="Edit in Google Calendar"
                      data-testid="edit-in-google-btn"
                    >
                      <Edit size={16} />
                    </a>
                  </>
                )}
                <button 
                  onClick={() => setShowEventModal(false)}
                  className="p-2 text-muted-foreground hover:text-foreground"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* EDIT MODE */}
            {isEditing ? (
              <div data-testid="edit-event-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Name</label>
                    <input type="text" value={editFormData.event_name} onChange={(e) => setEditFormData({...editFormData, event_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Type</label>
                    <select value={editFormData.event_type} onChange={(e) => setEditFormData({...editFormData, event_type: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground">
                      {['Wedding', 'Pre-wedding', 'Mehendi', 'Haldi', 'Sangeet', 'Reception', 'Engagement', 'Photoshoot', 'Vidhi', 'Baraat', 'Other'].map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Date</label>
                    <input type="date" value={editFormData.event_date} onChange={(e) => setEditFormData({...editFormData, event_date: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Start Time</label>
                    <input type="text" value={editFormData.start_time} onChange={(e) => setEditFormData({...editFormData, start_time: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" placeholder="09:00 AM" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">End Time</label>
                    <input type="text" value={editFormData.end_time} onChange={(e) => setEditFormData({...editFormData, end_time: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" placeholder="05:00 PM" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Location</label>
                    <input type="text" value={editFormData.location} onChange={(e) => setEditFormData({...editFormData, location: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Venue Address</label>
                    <input type="text" value={editFormData.venue_address} onChange={(e) => setEditFormData({...editFormData, venue_address: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Notes</label>
                    <textarea value={editFormData.event_notes} onChange={(e) => setEditFormData({...editFormData, event_notes: e.target.value})} className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground" rows="2" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={saveEvent} className="flex-1 bg-primary text-primary-foreground px-6 py-3 hover:bg-black/90" data-testid="save-event-btn">SAVE CHANGES</button>
                  <button onClick={() => setIsEditing(false)} className="px-6 py-3 border border-border hover:bg-accent">CANCEL</button>
                </div>
              </div>
            ) : showNotifyPanel ? (
              /* NOTIFY MODE */
              <div data-testid="notify-panel">
                <h3 className="text-lg font-medium mb-4">Notify Crew Members</h3>
                <div className="space-y-2 mb-6">
                  {eventDetails.assignments.map((assignment) => (
                    <label key={assignment.assignment_id} className="flex items-center gap-3 p-3 border border-border cursor-pointer hover:bg-accent">
                      <input
                        type="checkbox"
                        checked={selectedAssignments.includes(assignment.assignment_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAssignments([...selectedAssignments, assignment.assignment_id]);
                          } else {
                            setSelectedAssignments(selectedAssignments.filter(id => id !== assignment.assignment_id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{assignment.crew_member_name}</p>
                        <p className="text-xs text-muted-foreground">{assignment.crew_member_email} &bull; {assignment.crew_member_phone}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <button
                  onClick={sendReminderPush}
                  disabled={sendingNotification}
                  className="w-full flex items-center gap-3 p-4 border border-gold bg-gold/5 hover:bg-gold/10 transition-colors mb-3 disabled:opacity-50"
                  data-testid="notify-reminder-push-btn"
                >
                  <Bell className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">Send App Reminder</p>
                    <p className="text-xs text-muted-foreground">Push a reminder to their phone &amp; in-app inbox</p>
                  </div>
                </button>

                <button
                  onClick={() => sendNotification('email')}
                  disabled={sendingNotification || !notificationConfig?.email?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.email?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="notify-email-btn"
                >
                  <Mail className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">Send Email</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.email?.configured 
                        ? `From ${notificationConfig.email.sender}` 
                        : 'Not configured'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendNotification('sms')}
                  disabled={sendingNotification || !notificationConfig?.sms?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.sms?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="notify-sms-btn"
                >
                  <Phone className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">SMS / Text</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.sms?.configured 
                        ? `Send from ${notificationConfig.sms.from_number}` 
                        : 'Not configured'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendNotification('whatsapp')}
                  disabled={sendingNotification || !notificationConfig?.whatsapp?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.whatsapp?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="notify-whatsapp-btn"
                >
                  <MessageSquare className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.whatsapp?.configured 
                        ? `Send from ${notificationConfig.whatsapp.from_number}` 
                        : 'Not configured'}
                    </p>
                  </div>
                </button>

                {sendingNotification && (
                  <p className="text-sm text-muted-foreground text-center mb-3">Sending...</p>
                )}

                <button onClick={() => setShowNotifyPanel(false)} className="w-full px-6 py-3 border border-border hover:bg-accent">BACK</button>
              </div>
            ) : showEmailPanel ? (
              /* NOTIFY MODE (Google Calendar events) */
              <div data-testid="email-crew-panel">
                <h3 className="text-lg font-medium mb-2">Notify Crew Members</h3>
                <p className="text-sm text-muted-foreground mb-4">Select crew members to notify about this event</p>
                <div className="space-y-2 mb-4 max-h-[30vh] overflow-y-auto">
                  {crewMembers.map((crew) => (
                    <label key={crew.crew_id} className="flex items-center gap-3 p-3 border border-border cursor-pointer hover:bg-accent">
                      <input
                        type="checkbox"
                        checked={selectedCrewIds.includes(crew.crew_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCrewIds([...selectedCrewIds, crew.crew_id]);
                          } else {
                            setSelectedCrewIds(selectedCrewIds.filter(id => id !== crew.crew_id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{crew.full_name}</p>
                        <p className="text-xs text-muted-foreground">{crew.email} &bull; {crew.role}</p>
                      </div>
                    </label>
                  ))}
                </div>

                <button
                  onClick={() => sendQuickNotification('email')}
                  disabled={sendingNotification || selectedCrewIds.length === 0 || !notificationConfig?.email?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.email?.configured && selectedCrewIds.length > 0
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="send-quick-email-btn"
                >
                  <Mail className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">Send Email</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.email?.configured ? `From ${notificationConfig.email.sender}` : 'Not configured'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendQuickNotification('sms')}
                  disabled={sendingNotification || selectedCrewIds.length === 0 || !notificationConfig?.sms?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.sms?.configured && selectedCrewIds.length > 0
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="send-quick-sms-btn"
                >
                  <Phone className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">SMS / Text</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.sms?.configured ? `From ${notificationConfig.sms.from_number}` : 'Not configured'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendQuickNotification('whatsapp')}
                  disabled={sendingNotification || selectedCrewIds.length === 0 || !notificationConfig?.whatsapp?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors mb-3 ${
                    notificationConfig?.whatsapp?.configured && selectedCrewIds.length > 0
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                  data-testid="send-quick-whatsapp-btn"
                >
                  <MessageSquare className="text-gold" size={20} />
                  <div className="text-left flex-1">
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.whatsapp?.configured ? `From ${notificationConfig.whatsapp.from_number}` : 'Not configured'}
                    </p>
                  </div>
                </button>

                {sendingNotification && (
                  <p className="text-sm text-muted-foreground text-center mb-3">Sending...</p>
                )}

                <button onClick={() => setShowEmailPanel(false)} className="w-full px-6 py-3 border border-border hover:bg-accent">BACK</button>
              </div>
            ) : showAssignPanel ? (
              /* ASSIGN CREW MODE */
              <form onSubmit={handleAssignSubmit} data-testid="assign-crew-panel">
                <h3 className="text-lg font-medium mb-4">Assign Crew to {eventDetails.event.event_name}</h3>
                <div className="space-y-4 mb-6">
                  {assignments.map((assignment, index) => (
                    <div key={`assign-${index}`} className="border border-border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gold">Crew Member #{index + 1}</span>
                        {assignments.length > 1 && (
                          <button type="button" onClick={() => setAssignments(assignments.filter((_, i) => i !== index))} className="text-red-500 text-xs hover:underline">Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs mb-1 uppercase tracking-wide">Select Crew Member</label>
                          <select
                            value={assignment.crew_member_id}
                            onChange={(e) => {
                              const updated = [...assignments];
                              updated[index].crew_member_id = e.target.value;
                              setAssignments(updated);
                            }}
                            className="w-full px-3 py-2 border border-input bg-background text-sm"
                          >
                            <option value="">-- Select --</option>
                            {crewMembers.map(crew => (
                              <option key={crew.crew_id} value={crew.crew_id}>{crew.full_name} ({crew.role})</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs mb-1 uppercase tracking-wide">Assignment Type</label>
                          <select
                            value={assignment.day_type}
                            onChange={(e) => {
                              const updated = [...assignments];
                              updated[index].day_type = e.target.value;
                              setAssignments(updated);
                            }}
                            className="w-full px-3 py-2 border border-input bg-background text-sm"
                          >
                            <option value="half_day">Half Day</option>
                            <option value="full_day">Full Day</option>
                            <option value="custom">Custom Amount</option>
                          </select>
                        </div>
                      </div>
                      {assignment.day_type === 'custom' && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <label className="block text-xs mb-1 uppercase tracking-wide">Amount (USD)</label>
                            <input type="number" value={assignment.custom_amount_usd} onChange={(e) => { const u = [...assignments]; u[index].custom_amount_usd = parseFloat(e.target.value) || 0; setAssignments(u); }} className="w-full px-3 py-2 border border-input bg-background text-sm" min="0" step="0.01" />
                          </div>
                          <div>
                            <label className="block text-xs mb-1 uppercase tracking-wide">Amount (INR)</label>
                            <input type="number" value={assignment.custom_amount_inr} onChange={(e) => { const u = [...assignments]; u[index].custom_amount_inr = parseFloat(e.target.value) || 0; setAssignments(u); }} className="w-full px-3 py-2 border border-input bg-background text-sm" min="0" step="0.01" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setAssignments([...assignments, { crew_member_id: '', day_type: 'full_day', custom_amount_usd: 0, custom_amount_inr: 0 }])}
                  className="w-full py-2 rounded-xl border border-dashed border-black/20 text-foreground text-sm hover:bg-secondary mb-4"
                >
                  + ADD ANOTHER CREW MEMBER
                </button>
                <div className="flex gap-3">
                  <button type="submit" className="flex-1 bg-primary text-primary-foreground px-6 py-3 hover:bg-black/90" data-testid="assign-crew-submit">ASSIGN CREW</button>
                  <button type="button" onClick={() => setShowAssignPanel(false)} className="px-6 py-3 border border-border hover:bg-accent">BACK</button>
                </div>
              </form>
            ) : (
              /* VIEW MODE */
              <>
                {/* Project Info - only for app events */}
                {eventDetails.project && (
                  <div className="mb-6 p-4 bg-muted">
                    <p className="text-sm text-muted-foreground mb-1">Project</p>
                    <p className="text-lg font-medium">{eventDetails.project.project_name}</p>
                    {eventDetails.project.bride_name && eventDetails.project.groom_name && (
                      <p className="text-sm text-gold">{eventDetails.project.bride_name} & {eventDetails.project.groom_name}</p>
                    )}
                  </div>
                )}

                {/* Event Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Date</p>
                    <p className="font-medium">{formatDate(eventDetails.event.event_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Time</p>
                    <p className="font-medium">{eventDetails.event.start_time} - {eventDetails.event.end_time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Location</p>
                    <p className="font-medium">{eventDetails.event.location}</p>
                  </div>
                  {eventDetails.event.venue_address && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Venue</p>
                      <p className="font-medium">{eventDetails.event.venue_address}</p>
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eventDetails.event.venue_address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 px-4 py-2 bg-gold/10 text-gold hover:bg-gold/20 transition-colors text-xs inline-flex items-center gap-2"
                        data-testid="modal-directions-btn"
                      >
                        GET DIRECTIONS
                      </a>
                    </div>
                  )}
                </div>

                {/* Assigned Crew - only for app events */}
                {eventDetails.assignments && eventDetails.assignments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium mb-3">Assigned Crew ({eventDetails.assignments.length})</h3>
                    <div className="space-y-2">
                      {eventDetails.assignments.map((assignment) => {
                        const confirmStatus = assignment.confirmation_status || 'pending_confirmation';
                        return (
                        <div key={assignment.assignment_id} className={`flex items-center justify-between p-3 border ${
                          confirmStatus === 'declined' ? 'border-red-200 dark:border-red-500/30 bg-red-50/50 opacity-60' : 'border-border'
                        }`}>
                          <div>
                            <p className="font-medium">{assignment.crew_member_name}</p>
                            <p className="text-sm text-muted-foreground capitalize">{assignment.day_type.replace('_', ' ')}</p>
                          </div>
                          <div>
                            {confirmStatus === 'accepted' && (
                              <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400">Accepted</span>
                            )}
                            {confirmStatus === 'pending_confirmation' && (
                              <span className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">Awaiting</span>
                            )}
                            {confirmStatus === 'declined' && (
                              <span className="text-xs px-2 py-1 bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400">Declined</span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Event Notes */}
                {eventDetails.event.event_notes && (
                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-2">Notes</p>
                    <p className="text-sm">{eventDetails.event.event_notes}</p>
                  </div>
                )}

                {/* Contact Info - only for app events */}
                {eventDetails.project && (
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground mb-2">Client Contact</p>
                    <p className="text-sm">{eventDetails.project.client_email}</p>
                    <p className="text-sm">{eventDetails.project.client_phone}</p>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  {eventDetails.project ? (
                    <button
                      onClick={() => {
                        const pid = eventDetails.project.project_id;
                        setShowEventModal(false);
                        navigate(pid ? `/projects?project_id=${pid}` : '/projects');
                      }}
                      className="flex-1 bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90"
                      data-testid="view-project-btn"
                    >
                      VIEW PROJECT
                    </button>
                  ) : (
                    <a
                      href={eventDetails.event.google_link || 'https://calendar.google.com'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90 text-center"
                      data-testid="open-google-calendar-btn"
                    >
                      OPEN IN GOOGLE CALENDAR
                    </a>
                  )}
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="px-6 py-3 border border-border hover:bg-accent"
                  >
                    CLOSE
                  </button>
                </div>

                {/* Add to Calendar button */}
                <div className="mt-4">
                  <a
                    href={generateCalendarUrl(eventDetails.event, eventDetails.project)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full border border-black/15 text-foreground px-6 py-3 hover:bg-black hover:text-white transition-colors"
                    data-testid="add-to-calendar-btn"
                  >
                    <CalendarPlus size={18} /> Add to My Calendar
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
